import { t } from "i18next";
import _ from "lodash";
import { useEffect, useState } from "react";
import { HourHeatMap } from "./HourHeatMap";
import TimelineChart from "./TimelineChart";
import { useFetch } from "../../../../../hooks/useFetch";
import { useAppStore } from "../../../../../stores/RootStore";
import { PersonUsageRow, SummaryPersonUsageRow } from "../../../../../stores/TabsStore";
import { DateShift, Equipment, Event, EventsResponseItem, FShift, GrouppedData, Person } from "../../../../../types/types";
import Spinner from "../../../../../ui/Spinner/Spinner";
import { TbChevronDown, TbChevronUp } from "react-icons/tb";
import { transformEvent } from "../../../../../lib/calculations";

export function EquipmentInteractions({person, data, by}:{person: Person, data: SummaryPersonUsageRow, by: "days" | "equipment" | string}) {
  return (
    by === "equipment" ?
      <div className="flex flex-col space-y-2 my-6">
        <div className="grid grid-cols-12 text-sm px-4">
            <div className="col-span-8 flex space-x-2">
              <div className="w-6 text-center"></div>
              <div className="font-semibold">{t('equipment')}</div>
            </div>
          <div className="flex justify-end">{t('interaction')}</div>
          <div className="flex justify-end">{t('worked')}</div>
          <div className="flex justify-end">{t('stopped')}</div>
          <div className="flex justify-end">{t('turned_off')}</div>
        </div>
        {
          data.interactions.length > 0 ? data.interactions.map(eq => (
            <div className="border flex flex-col rounded text-sm">
              <div className="w-full flex p-4">
                <div className="flex flex-col w-full space-y-2">
                  <div className="grid grid-cols-12">
                    <div className="col-span-8 flex space-x-2">
                      <div className="w-6 text-center">{data.data.filter(d => d.interactionIds.includes(Number(eq.equipment.id))).length}</div>
                      <div className="font-semibold">{eq.equipment.attributes.name}</div>
                    </div>
                    <div className="flex justify-end">{eq.availability.total.time}</div>
                    <div className="flex justify-end">{eq.availability.worked.time}</div>
                    <div className="flex justify-end">{eq.availability.stopped.time}</div>
                    <div className="flex justify-end">{eq.availability.off.time}</div>
                  </div>
                </div>
              </div>
              <div>
                {
                  _.orderBy(data.data.filter(d => d.interactionIds.includes(Number(eq.equipment.id))), "dateShift.start_day", "desc").map(day => (
                    <EquipmentInteractionByEquipment person={person} equipment={eq.equipment} data={day}/>
                  ))
                }
              </div>
            </div>
          ))
          :
          <div>{t('no_data')}</div>
        }
      </div>
    :
    <EquipmentInteractionByDay person={person} data={data}/>
  )
}

function EquipmentInteractionByEquipment({person, equipment, data}:{person: Person, equipment: Equipment, data: PersonUsageRow}) {
  const shift = useAppStore().shifts.find(s => s.id === "full") as unknown as FShift
  let dayData = data.interactions.find(e => e.equipment.id === equipment.id)!
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [dayParams, setDayParams] = useState(new URLSearchParams({}));
  const [eventsData, setEventsData] = useState<Event[]>([])
  const [{ response: dayResponse, isLoading: dayLoading, error: dayError }, fetchDayData] =
  useFetch(`/directories/ng_equipment_item_events/events?${dayParams.toString()}`);

  useEffect(() => {
    if(!open || eventsData.length > 0) { return }
    setLoading(true)
    setParamsForDay()
    fetchDayData()
  }, [open])

  useEffect(() => {
    if(!dayResponse) { return }
    let response = dayResponse!.data as unknown as EventsResponseItem[]
    let ee = response[0].data as unknown as Event[]
    let transformedEvents = ee.map(e => transformEvent(e, equipment!))
    setEventsData(transformedEvents)
    setLoading(false)
  }, [dayResponse])

  function setParamsForDay() {
    let newDayParams = new URLSearchParams({});
    
    newDayParams.append("equipment_item_ids[]", equipment.id)
    newDayParams.append("from", dayData.start.startOf('day').toSQL()!);
    newDayParams.append("to", dayData.end.endOf('day').toSQL()!);
    newDayParams.append("day_time_from", dayData?.start.startOf('day').toFormat("HH:mm:ss")!);
    newDayParams.append("day_time_to", dayData?.end.endOf('day').toFormat("HH:mm:ss")!);

    setDayParams(newDayParams)
  }

  return (
    <div className="px-4 py-2 bg-muted border-border border-b last:border-0">
      <div className="flex flex-col space-y-2" >
        <div className="grid grid-cols-12">
          <div className="col-span-2 flex cursor-pointer items-center space-x-2 hover:text-primary" onClick={() => setOpen(!open)}>
            {open ? <TbChevronUp className="w-6" /> : <TbChevronDown className="w-6"/>}
            <div className="font-semibold">{dayData.start.toFormat("dd.MM.yyyy")}</div>
          </div>
          <div className="col-span-1">{data.cardIds.length > 0 && !(data.cardIds.length === 1 && data.cardIds[0] === 0) ? person.attributes.access_tokens.filter(c => data.cardIds.includes(c.id)).map(c => t(`${c.access_token_type!}`)).join(", ") : t(`${person.attributes.access_token_type!}`)}</div>
          <div className="col-span-2">{dayData.start.toFormat("HH:mm:ss")} - {dayData.end.toFormat("HH:mm:ss")}</div>
          <div className="col-span-3">{dayData.startWork.isValid ? <>{dayData.startWork.toFormat("HH:mm:ss")} - {dayData.endWork.toFormat("HH:mm:ss")}</> : "-"}</div>
          <div className="col-span-1 flex justify-end">{dayData.availability.on.time}</div>
          <div className="col-span-1 flex justify-end">{dayData.availability.worked.time}</div>
          <div className="col-span-1 flex justify-end">{dayData.availability.stopped.time}</div>
          <div className="col-span-1 flex justify-end">{dayData.availability.off.time}</div>
        </div>
        {open ? <div className="space-y-2 h-20">
          {loading ? <Spinner /> :
            <>
              <TimelineChart events={eventsData!} personId={Number(person.id)} from={dayData.start} to={dayData.end} shift={shift} height={16} width="100%"/>
              <HourHeatMap events={eventsData} personId={Number(person.id)} from={dayData.start} to={dayData.end} shift={shift} height={40} width="100%"/>
            </>
          }
        </div> : <></>}
      </div>
    </div>
  )
}

function EquipmentInteractionByDay({person, data}:{person: Person, data: SummaryPersonUsageRow}) {
  function getParamsByCardType(type: string, day: PersonUsageRow) {
    switch (type) {
      case "manual": return (
        <>
          <div className="col-span-1 flex justify-end">{day.manual.on.time}</div>
          <div className="col-span-1 flex justify-end">{day.manual.worked.time}</div>
          <div className="col-span-1 flex justify-end">{day.manual.stopped.time}</div>
          <div className="col-span-1 flex justify-end">{day.manual.off.time}</div>
        </>
      )
      case "adjustment": return (
        <>
          <div className="col-span-1 flex justify-end">{day.maintenance.on.time}</div>
          <div className="col-span-1 flex justify-end">{day.maintenance.worked.time}</div>
          <div className="col-span-1 flex justify-end">{day.maintenance.stopped.time}</div>
          <div className="col-span-1 flex justify-end">{day.maintenance.off.time}</div>
        </>
      )
      case "repair": return (
        <>
          <div className="col-span-1 flex justify-end">{day.repair.on.time}</div>
          <div className="col-span-1 flex justify-end">{day.repair.worked.time}</div>
          <div className="col-span-1 flex justify-end">{day.repair.stopped.time}</div>
          <div className="col-span-1 flex justify-end">{day.repair.off.time}</div>
        </>
      )
      case "service": return (
        <>
          <div className="col-span-1 flex justify-end">{day.service.on.time}</div>
          <div className="col-span-1 flex justify-end">{day.service.worked.time}</div>
          <div className="col-span-1 flex justify-end">{day.service.stopped.time}</div>
          <div className="col-span-1 flex justify-end">{day.service.off.time}</div>
        </>
      )
      default: return (
        <>
          <div className="col-span-1 flex justify-end">{day.manual.on.time}</div>
          <div className="col-span-1 flex justify-end">{day.manual.worked.time}</div>
          <div className="col-span-1 flex justify-end">{day.manual.stopped.time}</div>
          <div className="col-span-1 flex justify-end">{day.manual.off.time}</div>
        </>
      )
    }
  }

  return (
    <div className="flex flex-col space-y-2 my-6">
      <div className="grid grid-cols-12 text-sm px-4">
        <div className="col-span-2 flex space-x-2">
          <div className="font-semibold">{t('date')}</div>
        </div>
        <div className="col-span-2">{t('duration')}</div>
        <div className="col-span-2">{t('start')}</div>
        <div className="col-span-2">{t('finish')}</div>
        <div className="flex justify-end">{t('interaction')}</div>
        <div className="flex justify-end">{t('worked')}</div>
        <div className="flex justify-end">{t('stopped')}</div>
        <div className="flex justify-end">{t('turned_off')}</div>
      </div>
      {
        data.data.length > 0 ? _.orderBy(data.data, "dateShift.start_day", "desc").map(day => (
          <div className="border flex flex-col rounded text-sm">
            <div className="w-full flex flex-col">
              <div className="grid grid-cols-12 px-4 py-2">
                <div className="col-span-2 font-semibold">{day.dateShift.start_day.toFormat("dd.MM.yyyy")} ({day.dateShift.start_day.weekdayLong})</div>
                <div className="col-span-2">{day.start.toFormat("HH:mm:ss")} - {day.end.toFormat("HH:mm:ss")}</div>
                <div className="col-span-2">{day.startWork.isValid ? <>{day.startWork.toFormat("HH:mm:ss")}</> : "-"}</div>
                <div className="col-span-2">{day.endWork.isValid ? <>{day.endWork.toFormat("HH:mm:ss")}</> : "-"}</div>
                {getParamsByCardType(person.attributes.access_tokens.find(c => day.cardIds.includes(c.id))?.access_token_type!, day)}
              </div>
              {day.interactions.map(e => (
                <DayEquipmentInteraction person={person} equipment={e.equipment} data={data.data.find(r => r.dateShift.start_day.toSQLDate() === e.start.toSQLDate())!}/>
              ))}
            </div>
          </div>
        ))
        :
        <div>{t('no_data')}</div>
      }
    </div>
  )
}

function DayEquipmentInteraction({person, equipment, data}:{person: Person, equipment: Equipment, data: PersonUsageRow}) {
  const shift = useAppStore().shifts.find(s => s.id === "full") as unknown as FShift
  let dayData = data.interactions.find(e => e.equipment.id === equipment.id)!
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [dayParams, setDayParams] = useState(new URLSearchParams({}));
  const [eventsData, setEventsData] = useState<Event[]>([])
  const [{ response: dayResponse, isLoading: dayLoading, error: dayError }, fetchDayData] =
  useFetch(`/directories/ng_equipment_item_events/events?${dayParams.toString()}`);

  useEffect(() => {
    if(!open || eventsData.length > 0) { return }
    setLoading(true)
    setParamsForDay()
    fetchDayData()
  }, [open])

  useEffect(() => {
    if(!dayResponse) { return }
    let response = dayResponse!.data as unknown as EventsResponseItem[]
    let ee = response[0].data as unknown as Event[]
    let transformedEvents = ee.map(e => transformEvent(e, equipment!))
    setEventsData(transformedEvents)
    setLoading(false)
  }, [dayResponse])

  function setParamsForDay() {
    let newDayParams = new URLSearchParams({});
    
    newDayParams.append("equipment_item_ids[]", equipment.id)
    newDayParams.append("from", dayData.start.startOf('day').toSQL()!);
    newDayParams.append("to", dayData.end.endOf('day').toSQL()!);
    newDayParams.append("day_time_from", dayData?.start.startOf('day').toFormat("HH:mm:ss")!);
    newDayParams.append("day_time_to", dayData?.end.endOf('day').toFormat("HH:mm:ss")!);

    setDayParams(newDayParams)
  }

  return (
    <div className="px-4 py-2 bg-muted border-border border-b last:border-0">
      <div className="flex flex-col space-y-2" >
        <div className="grid grid-cols-12">
          <div className="col-span-8 flex cursor-pointer items-center space-x-2 hover:text-primary truncate" onClick={() => setOpen(!open)}>
            {open ? <TbChevronUp className="w-6" /> : <TbChevronDown className="w-6"/>}
            <div className="font-semibold">{dayData.equipment.attributes.name} ({data.cardIds.length > 0 && !(data.cardIds.length === 1 && data.cardIds[0] === 0) ? person.attributes.access_tokens.filter(c => data.cardIds.includes(c.id)).map(c => t(`${c.access_token_type!}`)).join(", ") : t(`${person.attributes.access_token_type!}`)})</div>
          </div>
          <div className="col-span-1 flex justify-end">{dayData.availability.on.time}</div>
          <div className="col-span-1 flex justify-end">{dayData.availability.worked.time}</div>
          <div className="col-span-1 flex justify-end">{dayData.availability.stopped.time}</div>
          <div className="col-span-1 flex justify-end">{dayData.availability.off.time}</div>
        </div>
        {open ? <div className="space-y-2 h-20">
          {loading ? <Spinner /> :
            <>
              <TimelineChart events={eventsData!} personId={Number(person.id)} from={dayData.start} to={dayData.end} shift={shift} height={16} width="100%"/>
              <HourHeatMap events={eventsData} personId={Number(person.id)} from={dayData.start} to={dayData.end} shift={shift} height={40} width="100%"/>
            </>
          }
        </div> : <></>}
      </div>
    </div>
  )
}